import { z } from "zod";
import { signatureSchema } from "./notes";

/**
 * Client forms (chunk C): intake, per-visit consent, and care instructions.
 * A form is a list of sections; wording is per clinic and versioned. "{clinic}"
 * in any text is replaced with the clinic's name.
 */

export type FormField =
  /** A paragraph of text. */
  | { kind: "text"; text: string }
  /** Bullet points. */
  | { kind: "bullets"; title?: string; items: string[] }
  /** Name, DOB, phone, email, address, emergency contact: filled in from the client record, and corrections update it. */
  | { kind: "client_details" }
  /** The linked guardian's name, relationship, and phone (minors). */
  | { kind: "guardian_details" }
  | { kind: "input"; key: string; label: string; multiline?: boolean; required?: boolean }
  /**
   * Tick any that apply. Options in `flag` are shown in red to the nurse. `none` ("None of the above")
   * clears the others. `details` asks for more when certain options are ticked.
   */
  | ({ kind: "checklist"; key: string; label: string; options: string[]; flag?: string[]; none?: string; required?: boolean } & ChoiceExtras)
  /** Pick one. */
  | ({ kind: "choice"; key: string; label: string; options: string[]; flag?: string[]; required?: boolean } & ChoiceExtras)
  /** A single box the signer must tick. */
  | { kind: "agree"; key: string; label: string };

/** Optional extras on checklist and choice questions. */
export interface ChoiceExtras {
  /** A text box shown when any of these options is picked. */
  details?: { key: string; label: string; forOptions: string[] };
  /** Not asked of clients whose sex at birth is male; this answer is used for them. */
  maleAnswer?: string;
  /** The Qualiphy question this answers: option wording → Qualiphy's answer ID. */
  qualiphy?: QualiphyLink;
}

export interface QualiphyLink {
  questionId: number;
  responses: Record<string, number>;
  /** Free text elsewhere on the form that goes with one of Qualiphy's answers (e.g. "other allergies"). */
  otherText?: { key: string; option: string };
}

/** One answer to send to Qualiphy (POST /api/exam_submit_answers). */
export interface QualiphyPrefill {
  questionId: number;
  answer: string;
  responseId?: number;
  answerText?: string;
}

export interface FormSection {
  title: string;
  /** Only for minors (guardian signs), or only for adults. */
  only?: "minor" | "adult";
  fields: FormField[];
}

export interface FormDefinition {
  title: string;
  subtitle?: string;
  sections: FormSection[];
  /** Qualiphy's "What is your current age?" question, answered from the date of birth. */
  qualiphyAgeQuestionId?: number;
  /** The Qualiphy exam whose questions this form's answers fill in (unset = the clinic's default exam). */
  qualiphyExamId?: number;
}

const ageFrom = (dob: string, today: Date) => {
  const [y, m, d] = dob.split("-").map(Number) as [number, number, number];
  let age = today.getUTCFullYear() - y;
  if (today.getUTCMonth() + 1 < m || (today.getUTCMonth() + 1 === m && today.getUTCDate() < d)) age--;
  return age;
};

/**
 * Turn a signed intake's answers into Qualiphy's screening answers, for the questions the form
 * links to Qualiphy. Answers whose wording no longer matches Qualiphy's are left out (Qualiphy then asks).
 */
export function qualiphyAnswersFrom(def: FormDefinition | undefined, answers: Record<string, unknown>, sexAtBirth: string | null, dob?: string | null, today = new Date()): QualiphyPrefill[] {
  if (!def) return [];
  const out: QualiphyPrefill[] = [];
  if (def.qualiphyAgeQuestionId && dob) out.push({ questionId: def.qualiphyAgeQuestionId, answer: String(ageFrom(dob, today)) });
  for (const section of def.sections) {
    for (const f of section.fields) {
      if ((f.kind !== "checklist" && f.kind !== "choice") || !f.qualiphy) continue;
      const q = f.qualiphy;
      let picked: string[] = [];
      if (sexAtBirth === "male" && f.maleAnswer) picked = [f.maleAnswer];
      else if (f.kind === "choice") picked = typeof answers[f.key] === "string" && answers[f.key] ? [answers[f.key] as string] : [];
      else picked = Array.isArray(answers[f.key]) ? (answers[f.key] as string[]) : [];
      const details = f.details && typeof answers[f.details.key] === "string" ? (answers[f.details.key] as string).trim() : "";
      const other = q.otherText && typeof answers[q.otherText.key] === "string" ? (answers[q.otherText.key] as string).trim() : "";
      if (other && !picked.includes(q.otherText!.option)) picked = [...picked.filter((p) => p !== (f.kind === "checklist" ? f.none : undefined)), q.otherText!.option];
      if (!picked.length) continue;
      for (const p of picked) {
        const id = q.responses[p];
        if (!id) continue;
        const text = q.otherText?.option === p && other ? other : f.details?.forOptions.includes(p) && details ? details : undefined;
        out.push({ questionId: q.questionId, answer: p, responseId: id, ...(text ? { answerText: text } : {}) });
      }
    }
  }
  return out;
}

export type FormValidity = { kind: "every_visit" } | { kind: "months"; months: number } | { kind: "once" };

export const validityLabel = (v: FormValidity) =>
  v.kind === "every_visit" ? "Every visit" : v.kind === "once" ? "Once" : `Valid ${v.months} months`;

// ── Field keys the app reads (health summary on charts and appointments)
export const HEALTH_KEYS = {
  conditions: "conditions",
  conditionsOther: "conditionsOther",
  allergies: "allergies",
  medications: "medications",
  changes: "changes",
  changesDetail: "changesDetail",
  pregnant: "pregnant",
  allergyList: "allergyList",
  medicationList: "medicationList",
} as const;

const fill = (s: string, clinic: string) => s.replaceAll("{clinic}", clinic);

/** Replace {clinic} throughout a definition. */
export function renderDefinition(def: FormDefinition, clinic: string): FormDefinition {
  return {
    title: fill(def.title, clinic),
    subtitle: def.subtitle && fill(def.subtitle, clinic),
    sections: def.sections.map((s) => ({
      ...s,
      title: fill(s.title, clinic),
      fields: s.fields.map((f) => {
        if (f.kind === "text") return { ...f, text: fill(f.text, clinic) };
        if (f.kind === "bullets") return { ...f, title: f.title && fill(f.title, clinic), items: f.items.map((i) => fill(i, clinic)) };
        if (f.kind === "agree" || f.kind === "input" || f.kind === "checklist") return { ...f, label: fill(f.label, clinic) };
        return f;
      }),
    })),
  };
}

// ── Default forms (VV's wording, typos fixed). New clinics start with these.

/** Every clinic has the intake form (key "intake"); other forms can be skipped on visits where it's sent. */
export const INTAKE_FORM_KEY = "intake";

export const DEFAULT_FORMS: { key: string; name: string; validity: FormValidity; sendWithIntake?: boolean; definition: FormDefinition }[] = [
  {
    key: "intake",
    name: "Intake",
    validity: { kind: "months", months: 12 },
    definition: {
      title: "Patient Intake Form",
      subtitle: "IV Hydration & IM Injections · Valid for 1 year from the date signed",
      qualiphyExamId: 4623,
      qualiphyAgeQuestionId: 82330,
      sections: [
        { title: "Personal information", fields: [{ kind: "client_details" }] },
        {
          title: "Health screening",
          fields: [
            { kind: "text", text: "These answers also go to the licensed provider who does your good faith exam, so you won't be asked again." },
            {
              kind: "choice",
              key: HEALTH_KEYS.pregnant,
              label: "Are you currently pregnant or breastfeeding?",
              options: ["Yes, Pregnant", "Yes, Breastfeeding", "No"],
              flag: ["Yes, Pregnant", "Yes, Breastfeeding"],
              required: true,
              maleAnswer: "No",
              qualiphy: { questionId: 82331, responses: {"Yes, Pregnant": 398279, "Yes, Breastfeeding": 398280, "No": 398281} },
            },
            {
              kind: "choice",
              key: "breastfeedingConsent",
              label: "If breastfeeding, do you agree to stop breastfeeding for 24 hours if you receive an IV or injection with vitamin or medication additives, or to receive only IV fluids with no additives?",
              options: ["Yes, I agree and consent to refrain from breastfeeding if receiving any IV ingredients other than Saline.", "No, I do not agree to stop breastfeeding for 24 hours after treatment", "N/A - Not breastfeeding"],
              flag: ["No, I do not agree to stop breastfeeding for 24 hours after treatment"],
              required: true,
              maleAnswer: "N/A - Not breastfeeding",
              qualiphy: { questionId: 82332, responses: {"Yes, I agree and consent to refrain from breastfeeding if receiving any IV ingredients other than Saline.": 398282, "No, I do not agree to stop breastfeeding for 24 hours after treatment": 398283, "N/A - Not breastfeeding": 398284} },
            },
            {
              kind: "checklist",
              key: "cancer",
              label: "Do you have any of the following?",
              options: ["Cancer: Any type, with any diagnosis or treatments within the past 12 months", "Cancer: Any type, with NO new diagnosis or treatments within the past 12 months.", "None of the above"],
              none: "None of the above",
              flag: ["Cancer: Any type, with any diagnosis or treatments within the past 12 months", "Cancer: Any type, with NO new diagnosis or treatments within the past 12 months."],
              required: true,
              qualiphy: { questionId: 82333, responses: {"Cancer: Any type, with any diagnosis or treatments within the past 12 months": 398285, "Cancer: Any type, with NO new diagnosis or treatments within the past 12 months.": 398286, "None of the above": 398287} },
            },
            {
              kind: "checklist",
              key: "injectionSite",
              label: "At the injection site, do you have any of the following?",
              options: ["Infection", "Permanent implants", "Skin Disorders", "Wounds, Inflammation", "None of the above"],
              none: "None of the above",
              flag: ["Infection", "Permanent implants", "Skin Disorders", "Wounds, Inflammation"],
              required: true,
              qualiphy: { questionId: 82334, responses: {"Infection": 398288, "Permanent implants": 398289, "Skin Disorders": 398290, "Wounds, Inflammation": 398291, "None of the above": 398292} },
            },
            {
              kind: "choice",
              key: "pastReaction",
              label: "Have you ever had a complication or serious adverse reaction to IV or IM nutrient therapy?",
              options: ["Yes, my reaction was the following:", "No"],
              flag: ["Yes, my reaction was the following:"],
              required: true,
              details: { key: "pastReactionDetail", label: "What happened?", forOptions: ["Yes, my reaction was the following:"] },
              qualiphy: { questionId: 82335, responses: {"Yes, my reaction was the following:": 398293, "No": 398294} },
            },
          ],
        },
        {
          title: "Medical history",
          fields: [
            {
              kind: "checklist",
              key: HEALTH_KEYS.conditions,
              label: "Do you have any of the following medical conditions?",
              options: ["Active Systemic Infection (Bacterial, Fungal, or Viral)", "Anemia due to blood loss /Hemolytic Anemia", "Asthma or Pulmonary Disease", "Bleeding or Clotting Disorder", "Cardiac Conditions (Congestive Heart Failure, uncontrolled Arrhythmias, QT Elongation Syndrome, Heart Attack or Stroke in the past year, etc.)", "Diabetes", "G6PD Deficiency (enzyme deficiency affecting red blood cells)", "Hemochromatosis (Iron overload)", "High or Low blood pressure", "Hypercalcemia (High calcium levels)", "Hyperthyroidism (Uncontrolled)", "Hypophosphatemia (Low phosphate levels)", "Hypothyroidism", "Kidney Disease", "Kidney-related condition, current or history (CKD, AKI, renal insufficiency, kidney stones, or dialysis)", "Liver Disease", "Leber's Disease (inherited vision loss)", "Neurological Abnormalities", "Sarcoidosis (an inflammatory disease that causes small lumps to form on organs)", "Seizure Disorder", "Wilson's Disease (Copper overload)", "None of the above"],
              none: "None of the above",
              flag: ["Active Systemic Infection (Bacterial, Fungal, or Viral)", "Anemia due to blood loss /Hemolytic Anemia", "Asthma or Pulmonary Disease", "Bleeding or Clotting Disorder", "Cardiac Conditions (Congestive Heart Failure, uncontrolled Arrhythmias, QT Elongation Syndrome, Heart Attack or Stroke in the past year, etc.)", "G6PD Deficiency (enzyme deficiency affecting red blood cells)", "Kidney Disease", "Kidney-related condition, current or history (CKD, AKI, renal insufficiency, kidney stones, or dialysis)"],
              required: true,
              qualiphy: { questionId: 82336, responses: {"Active Systemic Infection (Bacterial, Fungal, or Viral)": 398295, "Anemia due to blood loss /Hemolytic Anemia": 398296, "Asthma or Pulmonary Disease": 398297, "Bleeding or Clotting Disorder": 398298, "Cardiac Conditions (Congestive Heart Failure, uncontrolled Arrhythmias, QT Elongation Syndrome, Heart Attack or Stroke in the past year, etc.)": 398299, "Diabetes": 398300, "G6PD Deficiency (enzyme deficiency affecting red blood cells)": 398301, "Hemochromatosis (Iron overload)": 398302, "High or Low blood pressure": 398303, "Hypercalcemia (High calcium levels)": 398304, "Hyperthyroidism (Uncontrolled)": 398305, "Hypophosphatemia (Low phosphate levels)": 398306, "Hypothyroidism": 398307, "Kidney Disease": 398308, "Kidney-related condition, current or history (CKD, AKI, renal insufficiency, kidney stones, or dialysis)": 398309, "Liver Disease": 398310, "Leber's Disease (inherited vision loss)": 398311, "Neurological Abnormalities": 398312, "Sarcoidosis (an inflammatory disease that causes small lumps to form on organs)": 398313, "Seizure Disorder": 398314, "Wilson's Disease (Copper overload)": 398315, "None of the above": 398316} },
            },
            { kind: "input", key: HEALTH_KEYS.conditionsOther, label: "Other conditions / additional history", multiline: true },
          ],
        },
        {
          title: "Allergies & current medications",
          fields: [
            {
              kind: "checklist",
              key: HEALTH_KEYS.allergyList,
              label: "Are you allergic to any of the following?",
              options: ["Allergy to any medication, supplement, or injectable product:", "Any vitamin, mineral, amino acid, antioxidant, electrolyte, or nutrient given by IV or injection (including B12, B complex, vitamin C, vitamin D, iron, glutathione, NAD+, MIC, etc.)", "Benadryl (Diphenhydramine)", "Cobalt", "Famotidine (Pepcid)", "Lactated Ringer's or Normal Saline", "NAD+ (Nicotinamide Adenine Dinucleotide)", "Ondansetron (Zofran)", "Ozone", "Toradol (Ketorolac)", "No known allergies"],
              none: "No known allergies",
              flag: ["Allergy to any medication, supplement, or injectable product:", "Any vitamin, mineral, amino acid, antioxidant, electrolyte, or nutrient given by IV or injection (including B12, B complex, vitamin C, vitamin D, iron, glutathione, NAD+, MIC, etc.)", "Benadryl (Diphenhydramine)", "Cobalt", "Famotidine (Pepcid)", "Lactated Ringer's or Normal Saline", "NAD+ (Nicotinamide Adenine Dinucleotide)", "Ondansetron (Zofran)", "Ozone", "Toradol (Ketorolac)"],
              required: true,
              qualiphy: { questionId: 82337, responses: {"Allergy to any medication, supplement, or injectable product:": 398317, "Any vitamin, mineral, amino acid, antioxidant, electrolyte, or nutrient given by IV or injection (including B12, B complex, vitamin C, vitamin D, iron, glutathione, NAD+, MIC, etc.)": 398318, "Benadryl (Diphenhydramine)": 398319, "Cobalt": 398320, "Famotidine (Pepcid)": 398321, "Lactated Ringer's or Normal Saline": 398322, "NAD+ (Nicotinamide Adenine Dinucleotide)": 398323, "Ondansetron (Zofran)": 398324, "Ozone": 398325, "Toradol (Ketorolac)": 398326, "No known allergies": 398327}, otherText: { key: HEALTH_KEYS.allergies, option: "Allergy to any medication, supplement, or injectable product:" } },
            },
            { kind: "input", key: HEALTH_KEYS.allergies, label: "List all other allergies or anaphylaxis reactions (medications, foods, latex, contrast dye, etc.)", multiline: true },
            {
              kind: "checklist",
              key: HEALTH_KEYS.medicationList,
              label: "Are you taking any of the following medications?",
              options: ["Anticoagulants / Blood Thinners (Warfarin, Heparin, Apixaban, etc.)", "Antibiotics (Erythromycin, Clarithromycin, etc.)", "Antidepressants (specifically SSRIs or MAOIs): Lexapro, Prozac, Marplan, Nardil", "Antifungals (Fluconazole, Voriconazole, etc.)", "Antipsychotics (Haloperidol, Risperdone, Olanzapine, etc.)", "Cardiac Medications (including antiarrhythmics such as amiodarone, sotalol, dofetilide, dronedarone, digoxin, etc.)", "Chemotherapy (or any other type of cancer treatment, including radiation) within the past year", "Not currently taking any medications"],
              none: "Not currently taking any medications",
              flag: ["Anticoagulants / Blood Thinners (Warfarin, Heparin, Apixaban, etc.)", "Antibiotics (Erythromycin, Clarithromycin, etc.)", "Antidepressants (specifically SSRIs or MAOIs): Lexapro, Prozac, Marplan, Nardil", "Antifungals (Fluconazole, Voriconazole, etc.)", "Antipsychotics (Haloperidol, Risperdone, Olanzapine, etc.)", "Cardiac Medications (including antiarrhythmics such as amiodarone, sotalol, dofetilide, dronedarone, digoxin, etc.)", "Chemotherapy (or any other type of cancer treatment, including radiation) within the past year"],
              required: true,
              qualiphy: { questionId: 82338, responses: {"Anticoagulants / Blood Thinners (Warfarin, Heparin, Apixaban, etc.)": 398328, "Antibiotics (Erythromycin, Clarithromycin, etc.)": 398329, "Antidepressants (specifically SSRIs or MAOIs): Lexapro, Prozac, Marplan, Nardil": 398330, "Antifungals (Fluconazole, Voriconazole, etc.)": 398331, "Antipsychotics (Haloperidol, Risperdone, Olanzapine, etc.)": 398332, "Cardiac Medications (including antiarrhythmics such as amiodarone, sotalol, dofetilide, dronedarone, digoxin, etc.)": 398333, "Chemotherapy (or any other type of cancer treatment, including radiation) within the past year": 398334, "Other current medications:": 398335, "Not currently taking any medications": 398336}, otherText: { key: HEALTH_KEYS.medications, option: "Other current medications:" } },
            },
            { kind: "input", key: HEALTH_KEYS.medications, label: "List all other current medications & supplements", multiline: true },
          ],
        },
        {
          title: "Consent & acknowledgment",
          fields: [
            {
              kind: "text",
              text: "I authorize {clinic} and its licensed clinical staff to administer IV hydration therapy and/or intramuscular (IM) injections, including vitamins and related supplements, as discussed and agreed upon during my visit. I understand that these services are wellness-oriented and do not constitute a substitute for medical diagnosis or treatment by a licensed physician. I acknowledge that I have disclosed all known allergies, current medications, and relevant medical history on this form to the best of my knowledge. I understand there are inherent risks associated with IV and IM therapies, including but not limited to bruising, infection at the injection site, allergic reaction, or phlebitis, and I consent to treatment with this understanding. This consent is valid for one (1) year from the date of my signature.",
            },
            { kind: "agree", key: "accurate", label: "I confirm that all information provided on this form is accurate and complete to the best of my knowledge." },
            { kind: "agree", key: "consent", label: "I consent to the administration of IV hydration and/or IM injection services by {clinic} clinical staff and understand I may withdraw consent at any time." },
            { kind: "agree", key: "expires", label: "I understand this form expires one year from the date signed and I will be asked to complete it again at that time." },
          ],
        },
        {
          title: "Parent or guardian consent",
          only: "minor",
          fields: [
            { kind: "guardian_details" },
            {
              kind: "text",
              text: "I, the undersigned parent or legal guardian, authorize {clinic} and its licensed clinical staff to administer IV hydration therapy and/or intramuscular (IM) injections to the minor patient named above. I affirm that I am the parent or legal guardian of this minor and have the legal authority to provide this consent. I have reviewed and completed all sections of this form on behalf of my minor, and confirm that all information disclosed is accurate. I understand I may withdraw this consent at any time.",
            },
            { kind: "agree", key: "guardianAuthority", label: "I confirm I am the parent or legal guardian with authority to authorize treatment for this minor." },
            { kind: "agree", key: "guardianConsent", label: "I consent on behalf of my minor child to the administration of IV hydration and/or IM injection services by {clinic} clinical staff." },
          ],
        },
      ],
    },
  },
  {
    key: "consent",
    name: "Per-visit consent",
    validity: { kind: "every_visit" },
    // The intake asks everything this does, so a visit with the intake skips it.
    sendWithIntake: false,
    definition: {
      title: "Per-Visit Consent",
      subtitle: "IV Hydration & IM Injections · Please complete at each visit",
      sections: [
        {
          title: "Health updates since your last visit",
          fields: [
            {
              kind: "checklist",
              key: HEALTH_KEYS.changes,
              label: "Has any of the following changed since your last visit? Select all that apply.",
              options: [
                "New medications or supplements added",
                "Medications or supplements discontinued",
                "New allergies or reactions identified",
                "New diagnosis or medical condition",
                "Recent surgery or hospitalization",
                "Currently pregnant or breastfeeding",
              ],
              flag: ["New allergies or reactions identified", "New diagnosis or medical condition", "Recent surgery or hospitalization", "Currently pregnant or breastfeeding"],
            },
            { kind: "input", key: HEALTH_KEYS.changesDetail, label: "Please describe any changes or concerns", multiline: true },
          ],
        },
        {
          title: "Consent & acknowledgment",
          fields: [
            {
              kind: "text",
              text: "I voluntarily consent to receive IV hydration and/or intramuscular (IM) injection services administered by licensed clinical staff at {clinic}. I confirm that I have disclosed any changes to my health, medications, or allergies since my last visit. I understand the potential risks associated with IV and IM therapies, including bruising, infection at the injection site, and allergic reaction, and consent to proceed. I acknowledge these services are wellness-oriented and are not a substitute for medical care by a licensed physician. I may withdraw consent at any time.",
            },
            { kind: "agree", key: "consent", label: "I confirm the above information is accurate and I consent to today's services." },
          ],
        },
        {
          title: "Parent or guardian consent",
          only: "minor",
          fields: [
            { kind: "guardian_details" },
            {
              kind: "text",
              text: "I, the undersigned parent or legal guardian, authorize {clinic} and its licensed clinical staff to administer today's IV hydration and/or IM injection services to the minor patient named above. I confirm that the health update information provided on this form is accurate, and I consent to today's treatment on behalf of my minor. I understand I may withdraw consent at any time.",
            },
            { kind: "agree", key: "guardianAuthority", label: "I confirm I am the parent or legal guardian with authority to authorize today's treatment for this minor." },
          ],
        },
      ],
    },
  },
  {
    key: "care",
    name: "Pre & post care instructions",
    validity: { kind: "months", months: 12 },
    definition: {
      title: "Pre & Post Care Instructions",
      subtitle: "IV Hydration & IM Injections · Please read carefully before your appointment",
      sections: [
        {
          title: "Before your visit",
          fields: [
            {
              kind: "bullets",
              title: "Hydration",
              items: [
                "Drink at least 16–32 oz of water in the 2 hours before your appointment.",
                "Avoid excessive caffeine or alcohol the day before and day of your visit, as both can contribute to dehydration.",
              ],
            },
            { kind: "text", text: "Being well hydrated before your IV session makes it easier to access your veins, improves comfort, and helps you get the most out of your treatment." },
            {
              kind: "bullets",
              title: "Eating",
              items: [
                "Have a light meal or snack 1–2 hours before your appointment.",
                "Avoid arriving on an empty stomach; this can increase the likelihood of lightheadedness or nausea during treatment.",
                "Avoid heavy, greasy, or large meals immediately before your session.",
              ],
            },
            {
              kind: "bullets",
              title: "Medications & supplements",
              items: [
                "Continue taking all regularly prescribed medications as directed unless your provider has advised otherwise.",
                "Let us know at check-in if you have started any new medications or supplements since your last visit.",
              ],
            },
            {
              kind: "bullets",
              title: "What to wear",
              items: ["Wear loose, comfortable clothing with sleeves that can be easily rolled up above the elbow.", "Avoid tight-fitting tops or long sleeves that are difficult to move."],
            },
            {
              kind: "bullets",
              title: "What to bring",
              items: ["A valid photo ID for your first visit.", "Something to keep you comfortable during your session: a book, headphones, or a snack are all welcome."],
            },
            {
              kind: "bullets",
              title: "Before you arrive, let us know if you have",
              items: [
                "A known clotting or bleeding disorder.",
                "A history of difficult IV access or collapsed veins.",
                "Kidney disease, congestive heart failure, or pulmonary edema.",
                "Recently had surgery, been hospitalized, or started a new prescription.",
                "Any current infection, fever, or active illness.",
              ],
            },
          ],
        },
        {
          title: "After your visit",
          fields: [
            {
              kind: "bullets",
              title: "Hydration & nutrition",
              items: [
                "Continue drinking water throughout the rest of the day; aim for at least 8 additional glasses.",
                "Eat a balanced meal within a few hours of your session to help your body absorb and use the nutrients delivered.",
                "Limit alcohol for at least 24 hours after your treatment.",
              ],
            },
            {
              kind: "bullets",
              title: "Activity",
              items: ["Most patients feel energized after treatment and can return to normal daily activities immediately.", "Rest if you feel fatigued; this is a normal response as your body processes the infusion."],
            },
            {
              kind: "bullets",
              title: "Injection site care (IV & IM)",
              items: [
                "A small bruise, tenderness, or mild swelling at the injection or IV site is normal and typically resolves within 1–3 days.",
                "Apply a cool compress to the site for 10–15 minutes if there is any discomfort or swelling.",
                "Keep the site clean and dry for the rest of the day.",
                "Do not rub or massage the IM injection site; this can cause irritation.",
              ],
            },
            {
              kind: "bullets",
              title: "What to expect",
              items: [
                "Some patients notice benefits immediately; others may feel the full effect over the following 12–48 hours.",
                "Mild fatigue or a flushed feeling shortly after treatment is common and usually resolves within an hour.",
                "A slight taste of vitamins (particularly B vitamins) or a warm sensation during infusion is normal.",
              ],
            },
            {
              kind: "text",
              text: "When to seek care: contact us or seek medical attention if you experience severe pain, significant swelling or redness at the IV or injection site, difficulty breathing, chest pain, a rash or hives, fever above 101°F, or any symptom that feels unusual or concerning.",
            },
            {
              kind: "bullets",
              title: "Follow-up & scheduling",
              items: [
                "Most IV hydration and vitamin therapy protocols work best with consistent, regularly scheduled sessions. Ask your {clinic} team about a schedule that is right for your goals.",
                "If you have any questions or concerns after your visit, do not hesitate to reach out to us directly.",
              ],
            },
          ],
        },
        { title: "Acknowledgment", fields: [{ kind: "agree", key: "read", label: "I have read and understand these pre- and post-care instructions." }] },
      ],
    },
  },
];

// ── API shapes

export const formSubmitSchema = z.object({
  templateId: z.string().uuid(),
  versionId: z.string().uuid(),
  answers: z.record(z.string(), z.union([z.string().max(4000), z.boolean(), z.array(z.string().max(200)).max(50)])),
  /** Client record corrections from the "client_details" block. */
  details: z
    .object({
      firstName: z.string().trim().min(1).max(80),
      lastName: z.string().trim().min(1).max(80),
      dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      mobile: z.string().trim().max(30).nullable().default(null),
      email: z.string().trim().max(254).nullable().default(null),
      sexAtBirth: z.enum(["female", "male", "intersex", "unknown"]).nullable().default(null),
      address: z.object({ line1: z.string().trim().max(120), line2: z.string().trim().max(120).default(""), city: z.string().trim().max(80), state: z.string().trim().max(40), postalCode: z.string().trim().max(12) }).nullable().default(null),
      emergencyName: z.string().trim().max(120).nullable().default(null),
      emergencyPhone: z.string().trim().max(30).nullable().default(null),
    })
    .nullable()
    .default(null),
  guardian: z.object({ name: z.string().trim().min(1).max(160), relationship: z.string().trim().min(1).max(60), phone: z.string().trim().max(30).default("") }).nullable().default(null),
  /**
   * Typed full legal name: needed when it's the signature (typed instead of drawn) or a parent or
   * guardian signs for a child. Drawn by the client themselves, it's taken from their record.
   */
  signedName: z.string().trim().max(160).default(""),
  /** Drawn signature (the default); null when they chose to type their name to sign instead. */
  signature: signatureSchema.nullable().default(null),
});
export type FormSubmitInput = z.infer<typeof formSubmitSchema>;

export interface FormToFill {
  templateId: string;
  versionId: string;
  key: string;
  name: string;
  validity: FormValidity;
  definition: FormDefinition;
  /** Starting answers (the last signed copy of this form, for returning clients). */
  previous: Record<string, unknown> | null;
}

export interface FormsPacket {
  clinicName: string;
  client: {
    id: string;
    firstName: string;
    lastName: string;
    dob: string | null;
    minor: boolean;
    details: NonNullable<FormSubmitInput["details"]>;
    guardian: { name: string; relationship: string; phone: string } | null;
  };
  appointment: { id: string; start: string; serviceName: string } | null;
  /** Still needed for this visit. */
  needed: FormToFill[];
  /** Already signed and still valid. */
  done: { key: string; name: string; signedAt: string }[];
  /**
   * The health screening (good faith exam) that follows the forms: after_forms = the intake comes
   * first; ready = start it now; started = opened before (start again to go back in). Null = none.
   */
  screening: { state: "after_forms" | "ready" | "started" } | null;
}

export type FormNeed = { key: string; name: string; state: "missing" | "expired" | "resign" };

export interface FormsStatus {
  /** Forms this visit needs that aren't signed. */
  needed: FormNeed[];
  /** Private link for the client (with date of birth). */
  link: string | null;
}

export interface HealthSummary {
  conditions: string[];
  conditionsOther: string;
  allergies: string;
  medications: string;
  changes: string[];
  changesDetail: string;
  /** Things the nurse must notice (shown in red). */
  flags: string[];
  intakeSignedAt: string | null;
  /** Where the intake was signed when not here ("boulevard"): its answers, read as they are. */
  intakeSource: string | null;
  consentSignedAt: string | null;
}

export interface SubmissionView {
  id: string;
  key: string;
  name: string;
  version: number;
  signedAt: string;
  signedName: string;
  signer: "client" | "guardian";
  via: "link" | "kiosk" | "staff";
  appointmentId: string | null;
  /** Voided: kept on record, no longer counts. */
  voided: { at: string; by: string; reason: string } | null;
}

export const formVoidSchema = z.object({ reason: z.string().trim().min(1, "Give a reason").max(300) });

export interface SubmissionDetail extends SubmissionView {
  definition: FormDefinition;
  answers: Record<string, unknown>;
  details: FormSubmitInput["details"];
  guardian: FormSubmitInput["guardian"];
  ip: string | null;
  contentHash: string;
  /** Drawn signature, if they drew one. */
  signature: string | null;
}

export interface FormTemplateView {
  id: string;
  key: string;
  name: string;
  validity: FormValidity;
  /** Also sent on a visit where the intake form is due or signed (always true for the intake). */
  sendWithIntake: boolean;
  version: number;
  versionId: string;
  definition: FormDefinition;
  updatedAt: string;
}

export const formTemplateUpdateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  definition: z.custom<FormDefinition>((v) => !!v && typeof v === "object" && Array.isArray((v as FormDefinition).sections)),
  validity: z.union([z.object({ kind: z.literal("every_visit") }), z.object({ kind: z.literal("once") }), z.object({ kind: z.literal("months"), months: z.number().int().min(1).max(60) })]),
  /** Clients who signed an earlier version sign again. */
  requireResign: z.boolean().default(false),
  sendWithIntake: z.boolean().default(true),
});

/** The "Tap to confirm" page: the booking's visits (first names and times only), and whether forms come next. */
export interface ConfirmView {
  clinicName: string;
  visits: { firstName: string; day: string; time: string; service: string }[];
  /** Everyone in the booking is confirmed. */
  confirmed: boolean;
  /** The visits are over (the link has nothing left to confirm). */
  over: boolean;
  /** This person still has forms to fill out for it. */
  formsNeeded: boolean;
}
